import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Pencil, Trash2, Lock, Shield } from 'lucide-react';

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// Permission groups for the picker
const PERMISSION_GROUPS = [
  {
    label: 'Applications',
    perms: ['applications.view', 'applications.edit', 'applications.shortlist', 'applications.send_test', 'applications.delete', 'applications.export'],
  },
  {
    label: 'Intakes',
    perms: ['intakes.view', 'intakes.create', 'intakes.edit', 'intakes.delete', 'intakes.close_review'],
  },
  {
    label: 'Students',
    perms: ['students.view', 'students.enroll', 'students.edit', 'students.delete'],
  },
  {
    label: 'Settings',
    perms: ['settings.view', 'settings.edit'],
  },
  {
    label: 'Reports',
    perms: ['reports.view', 'reports.export'],
  },
  {
    label: 'Users',
    perms: ['users.view', 'users.create', 'users.edit', 'users.delete'],
  },
  {
    label: 'Roles',
    perms: ['roles.view', 'roles.create', 'roles.edit', 'roles.delete'],
  },
  {
    label: 'Dev Tools',
    perms: ['dev_tools.access'],
  },
];

export default function AdminRolesPage() {
  const [roles, setRoles] = useState([]);
  const [allPermissions, setAllPermissions] = useState({});
  const [loading, setLoading] = useState(true);

  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [form, setForm] = useState({ name: '', description: '', permissions: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rolesRes, permsRes] = await Promise.all([
        axios.get(`${API}/roles`, auth()),
        axios.get(`${API}/roles/permissions`, auth()),
      ]);
      setRoles(rolesRes.data);
      // Build permission description map
      const map = {};
      permsRes.data.forEach(({ permission, description }) => { map[permission] = description; });
      setAllPermissions(map);
    } catch (err) {
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  const openNewRole = () => {
    setEditingRole(null);
    setForm({ name: '', description: '', permissions: [] });
    setShowRoleDialog(true);
  };

  const openEditRole = (role) => {
    setEditingRole(role);
    setForm({ name: role.name, description: role.description || '', permissions: [...(role.permissions || [])] });
    setShowRoleDialog(true);
  };

  const togglePermission = (perm) => {
    setForm(f => ({
      ...f,
      permissions: f.permissions.includes(perm)
        ? f.permissions.filter(p => p !== perm)
        : [...f.permissions, perm],
    }));
  };

  const toggleGroupAll = (group) => {
    const allSelected = group.perms.every(p => form.permissions.includes(p));
    setForm(f => ({
      ...f,
      permissions: allSelected
        ? f.permissions.filter(p => !group.perms.includes(p))
        : [...new Set([...f.permissions, ...group.perms])],
    }));
  };

  const handleSaveRole = async () => {
    if (!form.name.trim()) {
      toast.error('Role name is required');
      return;
    }
    setSaving(true);
    try {
      if (editingRole) {
        await axios.patch(`${API}/roles/${editingRole.id}`, {
          name: form.name,
          description: form.description,
          permissions: form.permissions,
        }, auth());
        toast.success('Role updated');
      } else {
        await axios.post(`${API}/roles`, {
          name: form.name,
          description: form.description,
          permissions: form.permissions,
        }, auth());
        toast.success('Role created');
      }
      setShowRoleDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRole = async () => {
    setSaving(true);
    try {
      await axios.delete(`${API}/roles/${deleteTarget.id}`, auth());
      toast.success('Role deleted');
      setShowDeleteDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete role');
    } finally {
      setSaving(false);
    }
  };

  const isSuperAdminRole = (role) => role.is_system && role.name === 'super_admin';

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-playfair font-bold text-primary">Roles & Permissions</h1>
          <p className="text-sm text-muted-foreground mt-1">Define what each role can access in the portal</p>
        </div>
        <Button onClick={openNewRole} className="btn-primary gap-2">
          <Plus className="w-4 h-4" /> New Role
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading roles...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {roles.map((role) => (
            <Card key={role.id} className="relative">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {role.is_system ? (
                      <Lock className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    ) : (
                      <Shield className="w-4 h-4 text-primary flex-shrink-0" />
                    )}
                    <CardTitle className="text-base leading-tight">{role.name}</CardTitle>
                    {role.is_system && (
                      <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-medium uppercase tracking-wide flex-shrink-0">System</span>
                    )}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    {!isSuperAdminRole(role) && (
                      <Button variant="ghost" size="icon" title="Edit" onClick={() => openEditRole(role)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                    )}
                    {!role.is_system && (
                      <Button
                        variant="ghost" size="icon" title="Delete"
                        className="text-destructive hover:text-destructive"
                        onClick={() => { setDeleteTarget(role); setShowDeleteDialog(true); }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
                {role.description && (
                  <CardDescription className="text-xs mt-1">{role.description}</CardDescription>
                )}
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex items-center gap-2">
                  {(() => { const perms = role.permissions || []; const n = perms.length; return (<>
                  <span className="text-xs text-muted-foreground">
                    {n} permission{n !== 1 ? 's' : ''}
                  </span>
                  {n > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {PERMISSION_GROUPS.map(group => {
                        const count = group.perms.filter(p => perms.includes(p)).length;
                        if (count === 0) return null;
                        return (
                          <span key={group.label} className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                            {group.label} ({count}/{group.perms.length})
                          </span>
                        );
                      })}
                    </div>
                  )}
                  </>); })()}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Role Dialog */}
      <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRole ? 'Edit Role' : 'New Role'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="r-name">Role Name <span className="text-red-500">*</span></Label>
              <Input id="r-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Content Manager" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="r-desc">Description</Label>
              <Textarea id="r-desc" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="What can this role do?" rows={2} />
            </div>

            <div className="space-y-3">
              <Label>Permissions</Label>
              {PERMISSION_GROUPS.map(group => {
                const allSelected = group.perms.every(p => form.permissions.includes(p));
                const someSelected = group.perms.some(p => form.permissions.includes(p));
                return (
                  <div key={group.label} className="border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">{group.label}</span>
                      <button
                        type="button"
                        className="text-xs text-primary hover:underline"
                        onClick={() => toggleGroupAll(group)}
                      >
                        {allSelected ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>
                    <div className="space-y-1.5">
                      {group.perms.map(perm => (
                        <label key={perm} className="flex items-start gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            className="mt-0.5 accent-primary"
                            checked={form.permissions.includes(perm)}
                            onChange={() => togglePermission(perm)}
                          />
                          <div>
                            <span className="text-xs font-mono text-muted-foreground">{perm}</span>
                            {allPermissions[perm] && (
                              <p className="text-xs text-muted-foreground mt-0.5">{allPermissions[perm]}</p>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRoleDialog(false)}>Cancel</Button>
            <Button className="btn-primary" onClick={handleSaveRole} disabled={saving}>
              {saving ? 'Saving...' : editingRole ? 'Save Changes' : 'Create Role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Role</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the role <strong>{deleteTarget?.name}</strong>? Users assigned this role will lose their permissions.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteRole} disabled={saving}>
              {saving ? 'Deleting...' : 'Delete Role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
